import { UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import {
	ChangeEvent,
	ReactElement,
	useRef,
	ReactNode,
	RefObject,
	useContext
} from "react";

export type FileSelectorProps = {
	children: (onClick: () => void) => ReactNode;
	trackingId?: string;
	contentTypes?: readonly string[];
} & (
	{
		multiple: true;
		onChange(file: File[]): void;
	}
	|
	{
		multiple?: false;
		onChange(file: File): void;
	}
);

export const FileSelector = (props: FileSelectorProps): ReactElement => {
	const {
		onChange,
		trackingId,
		children,
		contentTypes,
		multiple
	} = props;

	const inputRef: RefObject<HTMLInputElement | null> = useRef<HTMLInputElement>(null);
	const { id } = useContext(UniqueInputIdContext);

	const clickHandler = (): void => {
		inputRef.current?.click();
	};

	const changeHandler = (args: ChangeEvent<HTMLInputElement>): void => {
		const input: HTMLInputElement = args.target;
		const files: FileList | null = input.files;
		try {
			if (multiple) {
				if (isNotNil(files)) {
					onChange(
						Array.from(files)
					);
				}
			} else {
				if (files?.length !== 1) {
					return;
				}
				const file: File = files[0];
				onChange(file);
			}
		} finally {
			input.value = "";
		}
	};

	return (
		<>
			{children(clickHandler)}
			<input
				{...setTrackingId(trackingId, "__input")}
				type="file"
				onChange={changeHandler}
				style={{ display: "none" }}
				ref={inputRef}
				accept={contentTypes?.join(",")}
				multiple={multiple}
				id={id}
			/>
		</>
	);
};
FileSelector.displayName = "FileSelector";